/**
 * The clinic session: who the caller is (`/me`), which of their organizations is in scope, and which location
 * they are working at. The server decides everything; this only remembers the choice and renders the pickers.
 */
import { ApiError } from "@allergy123/api-client";
import type { Location, MeResponse, PracticeSettings } from "@allergy123/contracts/clinical";
import { Button, Card, Select, Status } from "@allergy123/ui";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { getOrganizationId, getStoredLocationId, setOrganizationId, storeLocationId, useLoad } from "./api";
import { ATTESTATIONS, STATEMENT_VERSION } from "./lib/attestations";
import { errorText } from "./lib/errors";

export interface ClinicSession {
  me: MeResponse;
  organizationId: string;
  organizationName: string;
  /** The location the staff member is working at; null when the membership covers none (nothing to show). */
  location: Location | null;
  locations: Location[];
  permissions: Set<string>;
  /** The practice's clinical settings (D-014). */
  settings: PracticeSettings | null;
  /** Whether the practice collects this signature kind; true when it has never saved settings. */
  signatureEnabled(kind: string): boolean;
  /** The statement shown for a signature kind and the version recorded with it (Q-89). */
  statementFor(kind: string): { text: string; version: string };
  can(permission: "clinical:read" | "clinical:write" | "clinical:order" | "clinical:manage"): boolean;
  setLocation(locationId: string): void;
  switchOrganization(organizationId: string): void;
  reload(): void;
}

const Ctx = createContext<ClinicSession | null>(null);

export function useSession(): ClinicSession {
  const s = useContext(Ctx);
  if (!s) throw new Error("useSession must be used inside <SessionProvider>");
  return s;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [orgTick, setOrgTick] = useState(0);
  const me = useLoad((api) => api.me(), [orgTick]);
  const [locationId, setLocationId] = useState<string | undefined>(getStoredLocationId());

  // Once the server names the organization in scope, keep it so later calls carry the header (Q-87).
  useEffect(() => {
    if (me.data?.organization && getOrganizationId() !== me.data.organization.id) {
      setOrganizationId(me.data.organization.id);
      setOrgTick((t) => t + 1);
    }
  }, [me.data]);

  const value = useMemo<ClinicSession | null>(() => {
    const data = me.data;
    if (!data?.organization || !data.membership) return null;
    const permissions = new Set(data.membership.permissions);
    const location = data.locations.find((l) => l.locationId === locationId) ?? data.locations[0] ?? null;
    const switchOrganization = (id: string) => {
      setOrganizationId(id);
      storeLocationId(undefined);
      setLocationId(undefined);
      setOrgTick((t) => t + 1);
    };
    return {
      me: data,
      organizationId: data.organization.id,
      organizationName: data.organization.name,
      location,
      locations: data.locations,
      permissions,
      settings: data.settings,
      signatureEnabled: (kind) => (data.settings ? data.settings.collectSignatures && data.settings.signatureKinds.includes(kind as PracticeSettings["signatureKinds"][number]) : true),
      statementFor: (kind) => data.settings?.statements[kind] ?? { text: ATTESTATIONS[kind]?.statement ?? "", version: STATEMENT_VERSION },
      can: (p) => permissions.has(p) || (p === "clinical:read" && permissions.has("clinical:write")),
      setLocation: (id) => {
        storeLocationId(id);
        setLocationId(id);
      },
      switchOrganization,
      reload: me.reload,
    };
  }, [me.data, me.reload, locationId]);

  if (me.state === "loading" && !me.data) return <p className="p-6 text-ink-secondary">Loading your practice…</p>;
  if (me.state === "error") return <SessionProblem error={me.error} retry={me.reload} />;
  const data = me.data!;
  if (!data.organization) {
    return <OrganizationPicker organizations={data.organizations} onPick={(id) => { setOrganizationId(id); setOrgTick((t) => t + 1); }} />;
  }
  if (!data.organization.clinicalEntitled || !data.membership || !["clinical:read", "clinical:write"].some((p) => data.membership!.permissions.includes(p))) {
    return (
      <Card tone="tinted" className="m-6 max-w-[60ch]">
        <h1 className="mb-2 text-h2 font-semibold text-anchor">No clinical access</h1>
        <p className="mb-4 text-ink-secondary">
          {data.organization.name} {data.organization.clinicalEntitled ? "has the clinical product, but your membership has no clinical permission." : "does not have the clinical product."} Ask the practice administrator.
        </p>
        {data.organizations.length > 1 && <OrganizationPicker organizations={data.organizations} onPick={(id) => { setOrganizationId(id); setOrgTick((t) => t + 1); }} inline />}
      </Card>
    );
  }
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function SessionProblem({ error, retry }: { error?: Error; retry: () => void }) {
  const unavailable = error instanceof ApiError && (error.code === "clinical_unavailable" || error.status === 404);
  return (
    <Card tone="tinted" className="m-6 max-w-[60ch]">
      <h1 className="mb-2 text-h2 font-semibold text-anchor">{unavailable ? "The clinical API is not running here" : "Couldn't load your session"}</h1>
      <p className="mb-4 text-ink-secondary">{unavailable ? "The clinic app needs the API in database mode: run npm run api:dev:db (or npm run local:start) and reload." : errorText(error)}</p>
      <Button variant="secondary" surface="card" onClick={retry}>
        Try again
      </Button>
    </Card>
  );
}

function OrganizationPicker({ organizations, onPick, inline = false }: { organizations: MeResponse["organizations"]; onPick: (id: string) => void; inline?: boolean }) {
  const [choice, setChoice] = useState(organizations[0]?.id ?? "");
  const body = (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (choice) onPick(choice);
      }}
    >
      <Select label="Practice" options={organizations.map((o) => ({ value: o.id, label: o.name }))} value={choice} onChange={(e) => setChoice(e.target.value)} />
      <div>
        <Button type="submit" disabled={!choice}>
          Continue
        </Button>
      </div>
    </form>
  );
  if (inline) return body;
  return (
    <Card className="m-6 max-w-[60ch]">
      <h1 className="mb-2 text-h2 font-semibold text-anchor">Which practice are you working in?</h1>
      <p className="mb-4 text-ink-secondary">You belong to more than one. Everything you see and do applies to the one you pick; switch from the header at any time.</p>
      {organizations.length === 0 ? <Status kind="warning" label="Your account belongs to no practice." /> : body}
    </Card>
  );
}
